import pb from '../../lib/pocketbase';

export const EULA_STATEMENT = 'I read and understood the terms and conditions';
// Kept stable so existing acceptance rows still match this agreement.
export const EULA_DOCUMENT_ID = '1TcCzF6H9YtbwBKYdu6vBNVCIo5ZZauc54LKAgwuhy8I';
export const EULA_PDF_PATH = '/eula.pdf';
export const EULA_DOCUMENT_URL = EULA_PDF_PATH;

const ROLE_FIELDS = [
  { field: 'SuperAdmin', label: 'Master User' },
  { field: 'Administrator', label: 'Main Administrator' },
  { field: 'ARCChief', label: 'ARC Chief' },
];

let cachedUserId = null;
let cachedStatus = null;

export function ensureAuth() {
  try {
    const stored = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
    if (stored?.token) {
      pb.authStore.save(stored.token, stored.record || null);
    }
    return stored?.record || null;
  } catch (error) {
    return null;
  }
}

function idsInclude(value, userId) {
  if (!value) return false;
  if (Array.isArray(value)) return value.includes(userId);
  return value === userId;
}

export async function getPrivilegedRoles(userId) {
  ensureAuth();
  const list = await pb.collection('Customers').getList(1, 50, {
    filter: `SuperAdmin~"${userId}" || Administrator~"${userId}" || ARCChief~"${userId}"`,
    requestKey: null,
  });

  const roles = new Set();
  const companyIds = [];
  for (const company of list.items || []) {
    companyIds.push(company.id);
    for (const role of ROLE_FIELDS) {
      if (idsInclude(company[role.field], userId)) {
        roles.add(role.label);
      }
    }
  }

  return { roles: Array.from(roles), companyIds };
}

export function clearEulaStatusCache() {
  cachedUserId = null;
  cachedStatus = null;
}

export function markEulaAccepted(userId) {
  cachedUserId = userId;
  cachedStatus = {
    required: true,
    accepted: true,
    roles: cachedStatus?.roles || [],
    companyIds: cachedStatus?.companyIds || [],
    collectionMissing: false,
  };
}

export async function getEulaStatus(userId, { force = false } = {}) {
  if (!force && cachedStatus && cachedUserId === userId) {
    return cachedStatus;
  }

  const { roles, companyIds } = await getPrivilegedRoles(userId);
  if (roles.length === 0) {
    cachedUserId = userId;
    cachedStatus = {
      required: false,
      accepted: true,
      roles,
      companyIds,
      collectionMissing: false,
    };
    return cachedStatus;
  }

  try {
    const existing = await pb.collection('EulaAcceptances').getList(1, 1, {
      filter: `Contact = "${userId}" && DocumentId = "${EULA_DOCUMENT_ID}"`,
      requestKey: null,
    });
    cachedUserId = userId;
    cachedStatus = {
      required: true,
      accepted: existing.totalItems > 0,
      roles,
      companyIds,
      collectionMissing: false,
    };
    return cachedStatus;
  } catch (error) {
    const collectionMissing = error?.status === 404;
    cachedUserId = userId;
    cachedStatus = {
      required: true,
      accepted: false,
      roles,
      companyIds,
      collectionMissing,
      lookupError: collectionMissing ? null : (error?.message || 'Could not check EULA acceptance'),
    };
    return cachedStatus;
  }
}

async function fetchClientIp() {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    if (!response.ok) return '';
    const data = await response.json();
    return data?.ip || '';
  } catch (error) {
    return '';
  }
}

export async function updatePassword(userId, oldPassword, password, passwordConfirm) {
  ensureAuth();
  await pb.collection('Contacts').update(userId, {
    oldPassword,
    password,
    passwordConfirm,
  });
  try {
    const refreshed = await pb.collection('Contacts').authRefresh();
    const stored = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
    localStorage.setItem('pocketbase_auth', JSON.stringify({
      ...stored,
      token: pb.authStore.token,
      record: refreshed?.record || stored.record,
    }));
  } catch (error) {
    // The password change already succeeded. Keep the current session if refresh fails.
  }
}

export async function recordEulaAcceptance({ userId, roles, companyIds }) {
  ensureAuth();
  const ip = await fetchClientIp();
  return pb.collection('EulaAcceptances').create({
    Contact: userId,
    Roles: roles.join(', '),
    Statement: EULA_STATEMENT,
    DocumentId: EULA_DOCUMENT_ID,
    DocumentUrl: EULA_DOCUMENT_URL,
    IpAddress: ip,
    AcceptedAt: new Date().toISOString(),
    CompanyIds: companyIds.join(', '),
  });
}
